import React from 'react';
import {
    SxProps,
    Tooltip,
    TooltipProps,
    Typography,
    TypographyProps,
    useTheme,
} from '@mui/material';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { IconLookup, IconName } from '@fortawesome/fontawesome-svg-core';
import { alpha, Box } from '@mui/system';

const cleanStringPrefix = (iconName: string): string =>
    iconName.startsWith('fa-') ? iconName.slice(3) : iconName;

const toSharpSolid = (
    iconProp: FontAwesomeIconProps['icon'],
): IconLookup | FontAwesomeIconProps['icon'] => {
    if (Array.isArray(iconProp)) {
        return { prefix: 'fass', iconName: (cleanStringPrefix(iconProp[1]) as IconName) || '' };
    }

    if (typeof iconProp === 'string') {
        return { prefix: 'fass', iconName: cleanStringPrefix(iconProp) as IconName };
    }

    if (typeof iconProp === 'object') {
        return { prefix: 'fass', iconName: cleanStringPrefix(iconProp.iconName) as IconName };
    }

    return iconProp;
};

const toSharpRegular = (
    iconProp: FontAwesomeIconProps['icon'],
): IconLookup | FontAwesomeIconProps['icon'] => {
    if (Array.isArray(iconProp)) {
        return { prefix: 'fasr', iconName: (cleanStringPrefix(iconProp[1]) as IconName) || '' };
    }

    if (typeof iconProp === 'string') {
        return { prefix: 'fasr', iconName: cleanStringPrefix(iconProp) as IconName };
    }

    if (typeof iconProp === 'object') {
        return { prefix: 'fasr', iconName: cleanStringPrefix(iconProp.iconName) as IconName };
    }

    return iconProp;
};

export type FaOptionIconProps = {
    size?: FontAwesomeIconProps['size'];
    icon: FontAwesomeIconProps['icon'];
    option?: FontAwesomeIconProps['icon'];
    iconProps?: Omit<FontAwesomeIconProps, 'icon' | 'size'>;
    optionProps?: Omit<FontAwesomeIconProps, 'icon' | 'size'>;
    color?: TypographyProps['color'];
    optionColor?: TypographyProps['color'];
    tooltip?: TooltipProps['title'];
    withShape?: boolean;
    shapeSize?: number;
    shapeColor?: TypographyProps['color'];
    ariaLabel?: string;
    disabled?: boolean;
    sx?: SxProps;
};

export const FaOptionIcon: React.FunctionComponent<FaOptionIconProps> = function ({
    size = '1x',
    icon,
    iconProps = {},
    optionProps = {},
    option = undefined,
    color = undefined,
    optionColor = undefined,
    tooltip = undefined,
    withShape = false,
    shapeSize = 48,
    shapeColor = undefined,
    ariaLabel = undefined,
    disabled = false,
    sx = undefined,
}: FaOptionIconProps) {
    const theme = useTheme();

    const shape = (
        <svg
            width={shapeSize}
            height={shapeSize}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M16.3647 45.5366C8.59052 41.1825 4.01469 36.2267 1.37052 26.7742C-1.27365 17.3217 2.32052 12.7933 9.77802 8.16999C17.8688 3.10332 25.0572 1.51999 30.963 0.316656C36.8688 -0.886677 41.508 1.34582 44.9438 6.99832C48.3797 12.635 48.9022 18.2717 46.5905 23.845C44.2788 29.4183 42.6005 33.1708 37.7713 39.9C32.9422 46.6291 24.123 49.8591 16.3647 45.5366Z"
                fill={(shapeColor as string) || alpha(theme.palette.primary.main, 0.15)}
            />
        </svg>
    );

    let containerStyle: SxProps = {};
    let iconStyle: SxProps = { display: 'block' };
    let shapeStyle: SxProps = {};

    if (withShape) {
        containerStyle = {
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${shapeSize}px`,
            height: `${shapeSize}px`,
            marginBottom: '15px',
        };
        iconStyle = { display: 'block', position: 'relative', zIndex: 10 };
        shapeStyle = { position: 'absolute', top: 0, left: 0 };
    }

    const disabledStyles: SxProps = {
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
    };

    return (
        <Box data-testid="fa-option-icon">
            <Box sx={containerStyle}>
                <Tooltip title={disabled ? 'Désactivé' : tooltip}>
                    <Typography
                        sx={{
                            fontSize: 'inherit',
                            verticalAlign: 'middle',
                            ...disabledStyles,
                            ...sx,
                        }}
                        color={color || undefined}
                        component="span"
                    >
                        <Box
                            sx={iconStyle}
                            className={option ? 'fa-layers fa-fw fa-lg' : 'fa-lg'}
                        >
                            <FontAwesomeIcon
                                icon={toSharpRegular(icon)}
                                size={size}
                                title={ariaLabel}
                                {...iconProps}
                            />
                            {option ? (
                                <Typography
                                    sx={{
                                        fontSize: 'inherit',
                                    }}
                                    component="span"
                                    color={optionColor || undefined}
                                >
                                    <FontAwesomeIcon
                                        {...optionProps}
                                        icon={toSharpSolid(option)}
                                        size={size}
                                        transform="shrink-8 right-6 down-8"
                                    />
                                </Typography>
                            ) : null}
                        </Box>
                    </Typography>
                </Tooltip>
                {withShape ? <Box sx={shapeStyle}>{shape}</Box> : null}
            </Box>
        </Box>
    );
};
